import * as Net from 'util/Net';

import type {components, operations} from './generated/openapiV3Internal';
import {V3_INTERNAL_ROUTES, V3InternalRoute} from './routes';

/** The server prefix under which the internal V3 REST surface is served. */
export const V3_INTERNAL_BASE_PATH = '/api/v3-internal';

/** All operationIds available on the internal V3 REST surface (prefix-free, e.g. `Jellyfish.Create`). */
export type V3InternalOperationId = keyof operations;

/** The full set of component schemas from the generated spec, e.g. `V3InternalSchemas['Jellyfish']`. */
export type V3InternalSchemas = components['schemas'];

/** Extracts the `application/json` payload type out of a request/response content object. */
type JsonContent<Content> = Content extends {content: {'application/json': infer Json}} ? Json : never;

/**
 * The JSON request body type for an operation, or `never` when the operation takes no JSON body.
 *
 * `NonNullable` is resolved before {@link JsonContent} (rather than inlined) so that, for a concrete
 * operation whose `requestBody` is absent, the type correctly reduces to `never` instead of widening
 * to `unknown`.
 */
export type V3InternalRequestBody<Op extends V3InternalOperationId> = JsonContent<
  NonNullable<operations[Op]['requestBody']>
>;

/** The path parameters for an operation, or an empty record when the operation has none. */
export type V3InternalPathParams<Op extends V3InternalOperationId> = operations[Op] extends {
  parameters: {path: infer P};
}
  ? P
  : Record<string, never>;

/** The query parameters for an operation, or `never` when the operation has none. */
export type V3InternalQueryParams<Op extends V3InternalOperationId> = operations[Op] extends {
  parameters: {query: infer Q};
}
  ? Q
  : never;

type SuccessResponses<Op extends V3InternalOperationId> = operations[Op]['responses'];

/**
 * The union of JSON response bodies for the success statuses (200/201/202) of an operation. Async
 * task endpoints resolve to their 202 `AsyncTaskLink`-style body just like any other operation.
 */
type JsonSuccessResponse<Op extends V3InternalOperationId> =
  | (200 extends keyof SuccessResponses<Op> ? JsonContent<SuccessResponses<Op>[200]> : never)
  | (201 extends keyof SuccessResponses<Op> ? JsonContent<SuccessResponses<Op>[201]> : never)
  | (202 extends keyof SuccessResponses<Op> ? JsonContent<SuccessResponses<Op>[202]> : never);

/**
 * The response type for an operation: the JSON success body when one exists, or `unknown` for
 * operations without a JSON success body (e.g. 204 No Content, or non-JSON responses).
 */
export type V3InternalResponse<Op extends V3InternalOperationId> = [JsonSuccessResponse<Op>] extends [never]
  ? unknown
  : JsonSuccessResponse<Op>;

type EmptyPathParams = Record<string, never>;

type PathParamsArg<Op extends V3InternalOperationId> = [V3InternalPathParams<Op>] extends [EmptyPathParams]
  ? {pathParams?: undefined}
  : {pathParams: V3InternalPathParams<Op>};

type BodyArg<Op extends V3InternalOperationId> = [V3InternalRequestBody<Op>] extends [never]
  ? {body?: undefined}
  : {body: V3InternalRequestBody<Op>};

type QueryArg<Op extends V3InternalOperationId> = [V3InternalQueryParams<Op>] extends [never]
  ? {query?: undefined}
  : {query?: V3InternalQueryParams<Op>};

/**
 * The typed argument object for {@link callV3Internal}. `pathParams` and `body` are required only
 * when the operation declares them, and are forbidden otherwise, so callers get compile-time errors
 * for missing or extraneous fields.
 */
export type CallV3InternalArgs<Op extends V3InternalOperationId> = PathParamsArg<Op> &
  BodyArg<Op> &
  QueryArg<Op>;

/** Extracts the set of content-type keys (e.g. `'application/json'`) declared by a content object. */
type ContentTypeKeys<Content> = Content extends {content: infer C} ? keyof C : never;

/** The content types of an operation's request body (`never` when it has no body). */
type RequestContentTypes<Op extends V3InternalOperationId> = ContentTypeKeys<
  NonNullable<operations[Op]['requestBody']>
>;

/** The content types of an operation's success (200/201/202) responses. */
type SuccessResponseContentTypes<Op extends V3InternalOperationId> =
  | (200 extends keyof SuccessResponses<Op> ? ContentTypeKeys<SuccessResponses<Op>[200]> : never)
  | (201 extends keyof SuccessResponses<Op> ? ContentTypeKeys<SuccessResponses<Op>[201]> : never)
  | (202 extends keyof SuccessResponses<Op> ? ContentTypeKeys<SuccessResponses<Op>[202]> : never);

/**
 * The operationIds this JSON-only wrapper can safely call: those whose request body (when present)
 * and success responses (when they carry a body) are all `application/json`.
 *
 * Non-JSON operations — e.g. the YAML custom methods `Jellyfish.CreateFromYaml`
 * (`application/yaml` request and response) and `Jellyfish.SerializeToYaml`
 * (`application/yaml` response) — are excluded, because routing them through the JSON `Net` helpers
 * would send and/or parse the wrong content type. They remain in {@link V3_INTERNAL_ROUTES} for
 * drift detection but are not callable here; a caller that needs them should add a dedicated path.
 */
export type V3InternalJsonOperationId = {
  [Op in V3InternalOperationId]: [
    Exclude<RequestContentTypes<Op> | SuccessResponseContentTypes<Op>, 'application/json'>,
  ] extends [never]
    ? Op
    : never;
}[V3InternalOperationId];

interface UntypedArgs {
  pathParams?: Record<string, string | number>;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
}

function buildV3InternalUrl(
  pathTemplate: string,
  pathParams: Record<string, string | number> | undefined,
  query: Record<string, string | number | boolean | null | undefined> | undefined
): string {
  const filledPath = pathTemplate.replace(/\{([^}]+)\}/g, (_match, name: string) => {
    const value = pathParams?.[name];
    if (value == null) {
      throw new Error(`Missing path parameter "${name}" for V3 internal request to ${pathTemplate}`);
    }
    return encodeURIComponent(String(value));
  });
  const url = `${V3_INTERNAL_BASE_PATH}${filledPath}`;
  if (query == null) {
    return url;
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value != null) {
      search.append(key, String(value));
    }
  }
  const queryString = search.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Typed, thin wrapper over the existing HTTP layer ({@link Net}) for the internal V3 REST surface.
 *
 * Given an `operationId`, it substitutes path params into the route template from
 * {@link V3_INTERNAL_ROUTES}, prefixes `/api/v3-internal`, and dispatches through the matching
 * `Net` promise helper. Request bodies and responses are typed off the generated `operations`.
 *
 * Only JSON operations are callable ({@link V3InternalJsonOperationId}); non-JSON operations such as
 * the YAML custom methods are excluded at the type level so they can't be sent through the JSON
 * `Net` helpers.
 *
 * CSRF is handled by the underlying `Net` helpers (state-changing jQuery requests pick up the
 * `X-CSRFToken` header via the global `$.ajaxSetup` registered in `App.ts`); GET requests are exempt
 * and need no token. This wrapper does not alter that behavior.
 */
export function callV3Internal<Op extends V3InternalJsonOperationId>(
  operationId: Op,
  args: CallV3InternalArgs<Op>
): Promise<V3InternalResponse<Op>> {
  const route: V3InternalRoute = V3_INTERNAL_ROUTES[operationId];
  const {pathParams, body, query} = args as UntypedArgs;
  const url = buildV3InternalUrl(route.pathTemplate, pathParams, query);

  let response: Promise<unknown>;
  switch (route.method) {
    case 'GET':
      response = Net.getJSONPromise(url);
      break;
    case 'POST':
      response = Net.postJSONPromise({url, data: body});
      break;
    case 'PATCH':
      response = Net.patchJSONPromise({url, data: body});
      break;
    case 'PUT':
      response = Net.putJSONPromise({url, data: body});
      break;
    case 'DELETE':
      response = Net.deletePromise({url, data: body});
      break;
    default:
      throw new Error(`Unsupported V3 internal HTTP method: ${String(route.method)}`);
  }
  return response as Promise<V3InternalResponse<Op>>;
}
